import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { findSnapTarget } from '@/utils/snapToWall';

const COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#ec4899', '#14b8a6', '#f97316', '#6366f1',
];

export default function CanvasEditor({
  polygons = [], dimensions = {}, zoom = 1,
  selectedPolygonId, highlightedPolygonIds = [],
  mode = 'select', snapEnabled = true, confidenceThreshold = 0,
  onPolygonClick, onPolygonHover,
  onCreatePolygon, onUpdatePolygon, onDeletePolygon, onSeedDetect,
  loading = false,
}) {
  const svgRef = useRef(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [drawingPoints, setDrawingPoints] = useState([]);
  const [mousePos, setMousePos] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [dragPos, setDragPos] = useState(null);
  const [snapTarget, setSnapTarget] = useState(null);

  const imgW = dimensions.width || 1600;
  const imgH = dimensions.height || 1100;

  // Compute effective scale for label sizing — how much the viewBox is scaled on screen
  const effectiveScale = useMemo(() => {
    if (!svgRef.current) return Math.max(zoom, 0.15);
    return Math.max(zoom, 0.15);
  }, [zoom]);

  const colorMap = useMemo(() => {
    const m = {};
    polygons.forEach((p, i) => { m[p.id] = COLORS[i % COLORS.length]; });
    return m;
  }, [polygons]);

  // Reset drawing on mode change
  useEffect(() => {
    setDrawingPoints([]);
    setMousePos(null);
    setDragging(null);
    setDragPos(null);
    setSnapTarget(null);
  }, [mode]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape' && mode === 'draw') {
        setDrawingPoints([]);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [mode]);

  const svgCoords = useCallback((evt) => {
    const svg = svgRef.current;
    if (!svg) return [0, 0];
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX;
    pt.y = evt.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return [0, 0];
    const svgPt = pt.matrixTransform(ctm.inverse());
    return [Math.round(svgPt.x), Math.round(svgPt.y)];
  }, []);

  const handleCanvasClick = useCallback((evt) => {
    if (evt.target.closest('[data-vertex]') || evt.target.closest('[data-polygon]')) return;
    if (mode === 'draw') {
      const [x, y] = svgCoords(evt);
      // Use snapped position if available
      if (snapEnabled) {
        const snap = findSnapTarget(x, y, polygons, null);
        setDrawingPoints(prev => [...prev, snap ? [snap.x, snap.y] : [x, y]]);
      } else {
        setDrawingPoints(prev => [...prev, [x, y]]);
      }
    } else if (mode === 'seed') {
      const [x, y] = svgCoords(evt);
      onSeedDetect?.(x, y);
    }
  }, [mode, svgCoords, onSeedDetect, snapEnabled, polygons]);

  const handleCanvasDoubleClick = useCallback((evt) => {
    if (mode !== 'draw') return;
    evt.preventDefault();
    setDrawingPoints(prev => {
      let pts = [...prev];
      // Remove duplicate points from double-click
      while (pts.length > 3) {
        const last = pts[pts.length - 1];
        const prev2 = pts[pts.length - 2];
        if (Math.hypot(last[0] - prev2[0], last[1] - prev2[1]) < 15) {
          pts.pop();
        } else break;
      }
      if (pts.length >= 3) {
        onCreatePolygon?.(pts);
      }
      return [];
    });
    setMousePos(null);
  }, [mode, onCreatePolygon]);

  const handleMouseMove = useCallback((evt) => {
    if (mode === 'draw' || mode === 'seed') {
      const [x, y] = svgCoords(evt);
      // Snap for draw mode
      if (mode === 'draw' && snapEnabled) {
        const snap = findSnapTarget(x, y, polygons, null);
        setSnapTarget(snap);
        setMousePos(snap ? [snap.x, snap.y] : [x, y]);
      } else {
        setMousePos([x, y]);
        setSnapTarget(null);
      }
    }
    if (dragging) {
      evt.preventDefault();
      const [x, y] = svgCoords(evt);
      // Snap for edit (vertex drag) mode
      if (snapEnabled) {
        const snap = findSnapTarget(x, y, polygons, dragging.polygonId);
        setSnapTarget(snap);
        setDragPos(snap ? [snap.x, snap.y] : [x, y]);
      } else {
        setDragPos([x, y]);
        setSnapTarget(null);
      }
    }
  }, [mode, svgCoords, dragging, snapEnabled, polygons]);

  const handleVertexDown = useCallback((evt, polygonId, vertexIndex) => {
    if (mode !== 'edit') return;
    evt.stopPropagation();
    evt.preventDefault();
    setDragging({ polygonId, vertexIndex });
    const [x, y] = svgCoords(evt);
    setDragPos([x, y]);
  }, [mode, svgCoords]);

  const handleMouseUp = useCallback(() => {
    if (dragging && dragPos) {
      const poly = polygons.find(p => p.id === dragging.polygonId);
      if (poly) {
        const newVerts = poly.polygon.map((v, i) =>
          i === dragging.vertexIndex ? dragPos : [...v]
        );
        onUpdatePolygon?.(dragging.polygonId, { vertices: newVerts });
      }
    }
    setDragging(null);
    setDragPos(null);
    setSnapTarget(null);
  }, [dragging, dragPos, polygons, onUpdatePolygon]);

  const handlePolygonClick = useCallback((evt, polygon) => {
    evt.stopPropagation();
    if (mode === 'delete') {
      onDeletePolygon?.(polygon.id);
    } else {
      onPolygonClick?.(polygon);
    }
  }, [mode, onPolygonClick, onDeletePolygon]);

  const isEditorMode = mode !== 'select' && mode !== 'pan';
  const needsCapture = mode === 'draw' || mode === 'seed';
  const cursorClass = {
    draw: 'cursor-crosshair', seed: 'cursor-crosshair',
    edit: dragging ? 'cursor-grabbing' : 'cursor-default',
    delete: 'cursor-pointer', select: 'cursor-default',
    pan: 'cursor-grab',
  }[mode] || 'cursor-default';

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${imgW} ${imgH}`}
      className={`absolute inset-0 w-full h-full ${cursorClass}`}
      style={{ pointerEvents: isEditorMode ? 'all' : 'none' }}
      onClick={handleCanvasClick}
      onDoubleClick={handleCanvasDoubleClick}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={() => { handleMouseUp(); setMousePos(null); }}
      data-testid="canvas-editor"
    >
      {/* Background capture for draw/seed */}
      {needsCapture && (
        <rect x="0" y="0" width={imgW} height={imgH} fill="transparent" />
      )}

      {/* Existing polygons */}
      {polygons.map((poly) => {
        const isEditing = mode === 'edit' && poly.id === selectedPolygonId;
        const verts = isEditing && dragging?.polygonId === poly.id
          ? poly.polygon.map((v, i) => i === dragging.vertexIndex ? dragPos : v)
          : poly.polygon;
        const pts = (verts || []).map(p => `${p[0]},${p[1]}`).join(' ');
        if (!pts) return null;

        const isSelected = poly.id === selectedPolygonId;
        const isHighlighted = highlightedPolygonIds.includes(poly.id);
        const isHovered = poly.id === hoveredId;
        const color = colorMap[poly.id] || '#3b82f6';
        const active = isSelected || isHighlighted || isHovered;
        const isDeleteTarget = mode === 'delete' && isHovered;

        const label = poly.room_number || `R${polygons.indexOf(poly) + 1}`;
        const cx = isEditing && dragging ? null : poly.centroid?.x;
        const cy = isEditing && dragging ? null : poly.centroid?.y;

        // Compute bounding box for dynamic label sizing
        const xs = (verts || []).map(v => v[0]);
        const ys = (verts || []).map(v => v[1]);
        const bboxW = xs.length > 0 ? Math.max(...xs) - Math.min(...xs) : 100;
        const bboxH = ys.length > 0 ? Math.max(...ys) - Math.min(...ys) : 100;
        // Size label to fit: use ~60% of the smaller dimension, capped by text length
        const maxFontByWidth = (bboxW * 0.7) / Math.max(label.length * 0.6, 1);
        const maxFontByHeight = bboxH * 0.3;
        const fontSize = Math.max(8, Math.min(maxFontByWidth, maxFontByHeight, 60));
        const areaFontSize = fontSize * 0.7;

        return (
          <g key={poly.id} data-testid={`polygon-${poly.id}`}>
            <polygon
              points={pts}
              fill={isDeleteTarget ? '#ef444440' : active ? `${color}30` : `${color}15`}
              stroke={isDeleteTarget ? '#ef4444' : active ? color : `${color}80`}
              strokeWidth={active ? 2.5 : 1.5}
              vectorEffect="non-scaling-stroke"
              className="pointer-events-auto cursor-pointer"
              data-polygon="true"
              onClick={(e) => handlePolygonClick(e, poly)}
              onMouseEnter={() => { setHoveredId(poly.id); onPolygonHover?.(poly); }}
              onMouseLeave={() => { setHoveredId(null); onPolygonHover?.(null); }}
            />
            {isSelected && !isEditing && (
              <polygon points={pts} fill="none" stroke={color}
                strokeWidth={3} opacity={0.8} strokeDasharray="8,4"
                vectorEffect="non-scaling-stroke"
                className="pointer-events-none" />
            )}

            {/* Room label — sized to fit inside the polygon */}
            {cx != null && cy != null && (
              <text x={cx} y={cy + fontSize * 0.35} textAnchor="middle"
                fill={active ? 'white' : color}
                fontSize={fontSize}
                fontFamily="monospace" fontWeight="bold"
                opacity={active ? 1 : 0.85}
                className="pointer-events-none"
                style={{ textShadow: '0 0 4px rgba(0,0,0,0.8), 0 0 8px rgba(0,0,0,0.5)' }}>
                {label}
              </text>
            )}
            {cx != null && cy != null && active && poly.area_sqft && (
              <text x={cx} y={cy + fontSize * 0.35 + areaFontSize * 1.3} textAnchor="middle"
                fill="#34d399" fontSize={areaFontSize} fontFamily="monospace"
                fontWeight="bold" className="pointer-events-none"
                style={{ textShadow: '0 0 4px rgba(0,0,0,0.8)' }}>
                {poly.area_sqft} SF
              </text>
            )}

            {/* Low-confidence warning indicator */}
            {confidenceThreshold > 0 && (poly.confidence || 0) > 0 && (poly.confidence || 0) < confidenceThreshold && (
              <g className="pointer-events-none" data-testid={`low-conf-${poly.id}`}>
                {/* Dashed border to flag low confidence */}
                <polygon points={pts} fill="none"
                  stroke="#f59e0b" strokeWidth={2.5} opacity={0.7}
                  strokeDasharray="4,4" vectorEffect="non-scaling-stroke" />
                {/* Warning badge at top-right of bbox */}
                {(() => {
                  const minX = Math.min(...(verts || []).map(v => v[0]));
                  const minY = Math.min(...(verts || []).map(v => v[1]));
                  return (
                    <g transform={`translate(${minX},${minY})`}>
                      <g transform={`scale(${1 / effectiveScale})`}>
                        <rect x={-2} y={-14} width={38} height={14} rx={2} fill="#78350F" stroke="#f59e0b" strokeWidth={1} opacity={0.9} />
                        <text x={17} y={-3} textAnchor="middle" fill="#fbbf24" fontSize={8} fontFamily="monospace" fontWeight="bold">
                          {Math.round((poly.confidence || 0) * 100)}%
                        </text>
                      </g>
                    </g>
                  );
                })()}
              </g>
            )}

            {/* Vertex handles in edit mode */}
            {isEditing && (verts || []).map((v, i) => (
              <circle key={i} cx={v[0]} cy={v[1]} r={7 / effectiveScale}
                fill={dragging?.vertexIndex === i ? '#f59e0b' : '#3b82f6'}
                stroke="white" strokeWidth={2}
                vectorEffect="non-scaling-stroke"
                className="pointer-events-auto cursor-grab"
                data-vertex="true"
                onMouseDown={(e) => handleVertexDown(e, poly.id, i)}
                data-testid={`vertex-${poly.id}-${i}`} />
            ))}
          </g>
        );
      })}

      {/* Drawing preview */}
      {mode === 'draw' && drawingPoints.length > 0 && (
        <g data-testid="drawing-preview" className="pointer-events-none">
          {drawingPoints.map((pt, i) => {
            if (i === 0) return null;
            const prev = drawingPoints[i - 1];
            return (
              <line key={i} x1={prev[0]} y1={prev[1]} x2={pt[0]} y2={pt[1]}
                stroke="#f59e0b" strokeWidth={2.5} strokeDasharray="6,3"
                vectorEffect="non-scaling-stroke" />
            );
          })}
          {drawingPoints.length >= 2 && (
            <line x1={drawingPoints[drawingPoints.length - 1][0]}
              y1={drawingPoints[drawingPoints.length - 1][1]}
              x2={drawingPoints[0][0]} y2={drawingPoints[0][1]}
              stroke="#f59e0b40" strokeWidth={1.5} strokeDasharray="4,4"
              vectorEffect="non-scaling-stroke" />
          )}
          {mousePos && (
            <line x1={drawingPoints[drawingPoints.length - 1][0]}
              y1={drawingPoints[drawingPoints.length - 1][1]}
              x2={mousePos[0]} y2={mousePos[1]}
              stroke="#f59e0b80" strokeWidth={1.5} strokeDasharray="4,4"
              vectorEffect="non-scaling-stroke" />
          )}
          {drawingPoints.map((pt, i) => (
            <circle key={`v${i}`} cx={pt[0]} cy={pt[1]} r={5 / effectiveScale}
              fill={i === 0 ? '#10b981' : '#f59e0b'} stroke="white" strokeWidth={2}
              vectorEffect="non-scaling-stroke" />
          ))}
          {drawingPoints.length >= 1 && (
            <text x={drawingPoints[0][0]} y={drawingPoints[0][1] - 12}
              textAnchor="middle" fill="#10b981" fontSize={10}
              fontFamily="monospace" fontWeight="bold">
              {drawingPoints.length >= 3 ? 'Double-click to close' : `${3 - drawingPoints.length} more pts`}
            </text>
          )}
        </g>
      )}

      {/* Seed crosshair */}
      {mode === 'seed' && mousePos && !loading && (
        <g className="pointer-events-none" data-testid="seed-crosshair">
          <line x1={mousePos[0] - 18 / effectiveScale} y1={mousePos[1]} x2={mousePos[0] + 18 / effectiveScale} y2={mousePos[1]}
            stroke="#10b981" strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
          <line x1={mousePos[0]} y1={mousePos[1] - 18 / effectiveScale} x2={mousePos[0]} y2={mousePos[1] + 18 / effectiveScale}
            stroke="#10b981" strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
          <circle cx={mousePos[0]} cy={mousePos[1]} r={10 / effectiveScale} fill="none"
            stroke="#10b981" strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
          <g transform={`translate(${mousePos[0]},${mousePos[1]})`}>
            <g transform={`scale(${1 / effectiveScale})`}>
              <text x={16} y={-10}
                fill="#10b981" fontSize={10} fontFamily="monospace">
                Click to detect room
              </text>
            </g>
          </g>
        </g>
      )}

      {/* Snap indicator */}
      {snapTarget && (mode === 'edit' || mode === 'draw') && (
        <g className="pointer-events-none" data-testid="snap-indicator">
          {/* Snap crosshair */}
          <circle cx={snapTarget.x} cy={snapTarget.y}
            r={8 / effectiveScale} fill="none"
            stroke={snapTarget.type === 'vertex' ? '#10b981' : '#f59e0b'}
            strokeWidth={2} vectorEffect="non-scaling-stroke"
            opacity={0.9} />
          <circle cx={snapTarget.x} cy={snapTarget.y}
            r={3 / effectiveScale}
            fill={snapTarget.type === 'vertex' ? '#10b981' : '#f59e0b'}
            opacity={0.9} />
          {/* Edge highlight */}
          {snapTarget.type === 'edge' && snapTarget.edgeFrom && snapTarget.edgeTo && (
            <line
              x1={snapTarget.edgeFrom[0]} y1={snapTarget.edgeFrom[1]}
              x2={snapTarget.edgeTo[0]} y2={snapTarget.edgeTo[1]}
              stroke="#f59e0b" strokeWidth={3} opacity={0.5}
              vectorEffect="non-scaling-stroke" strokeDasharray="6,3"
            />
          )}
          {/* Label */}
          <g transform={`translate(${snapTarget.x},${snapTarget.y})`}>
            <g transform={`scale(${1 / effectiveScale})`}>
              <text x={14} y={-6}
                fill={snapTarget.type === 'vertex' ? '#10b981' : '#f59e0b'}
                fontSize={9} fontFamily="monospace" fontWeight="bold">
                {snapTarget.type === 'vertex' ? 'SNAP: CORNER' : 'SNAP: WALL'}
              </text>
            </g>
          </g>
        </g>
      )}

      {/* Loading */}
      {loading && (
        <g data-testid="canvas-loading" className="pointer-events-none">
          <rect x={imgW / 2 - 70} y={imgH / 2 - 18} width={140} height={36}
            rx={6} fill="#0F172AE0" stroke="#3b82f6" strokeWidth={1.5} />
          <text x={imgW / 2} y={imgH / 2 + 5} textAnchor="middle"
            fill="#3b82f6" fontSize={12} fontFamily="monospace" fontWeight="bold">
            Detecting...
          </text>
        </g>
      )}
    </svg>
  );
}
